// PO-16 y PO-18 automáticos desde el Plan de Emergencias v2 (PE-B1).
//  - PO-16: cumple si el plan tiene una versión aprobada vigente; sin ninguna, no cumple.
//  - PO-18 (PE-39, aprobada por Kevin): cumple si todas las acciones obligatorias del plan están cerradas con
//    evidencia; en proceso si hay abiertas pero ninguna vencida; no cumple si alguna está vencida. Sin ninguna
//    acción: pendiente (PE-A4c). Con acciones pero sin obligatorias vigentes: cumple si hay una versión aprobada
//    (si no, queda pendiente). Una obligatoria con "origen
//    resuelto" ya no cuenta. Una "cerrada" sin evidencia cuenta como abierta. Evidencia (PE-A4): una fila de
//    emergency_plan_action_evidence (M37) o el evidence_path de la acción (M33). Sin M37 en la base, solo cuenta
//    evidence_path.
// Solo para empresas que usan la v2 (tienen versiones, sectores o evaluaciones del plan): EmergencyPlanPage y las
// empresas que solo usan la pantalla vieja no cambian.
// Estado manual (PE-B2b, decidido por Claude, igual para PO-16, PO-17 y PO-18): si el ítem tiene un estado marcado a
// mano en el Checklist (auto_source NULL), la sincronización NUNCA lo sobrescribe, ni con "Cumple", ni con "No cumple",
// ni con "En proceso", tampoco al borrar datos. El ítem muestra lo que dice el cálculo y el botón "Usar cálculo
// automático" (aplicarCalculoPlanV2) quita lo manual y escribe el cálculo. Sin estado manual, se escribe el cálculo.
// Nunca se toca un "No aplica".
//  - PO-17 (PE-B2, decidido por Claude: en este mismo archivo): simulacros de la empresa (emergency_drills, también
//    los de la pantalla anterior). Regla en estadoPO17 (pe-simulacros.ts): cumple con uno realizado y con el informe
//    firmado en los últimos 12 meses; en proceso con uno programado desde hoy o uno realizado sin firmar; si no, no
//    cumple. El sync devuelve, para los tres ítems, el estado calculado y su traza para mostrarlos en el Checklist.

import { supabase } from '../supabaseClient';
import type { FilaAccion } from './pe/pe-acciones';
import { estadoPO17, ResultadoPO17 } from './pe/pe-simulacros';
import type { Traza } from './pe/pe-tipos';
import { plural } from './plurales';

export const FUENTE_PLAN_V2 = 'plan_emergencias_v2';
export type EstadoItem = 'cumple' | 'en_proceso' | 'no_cumple' | 'pendiente';

interface VersionMin { status: string }
type AccionMin = Pick<FilaAccion, 'is_mandatory' | 'status' | 'evidence_path' | 'due_date' | 'source_resolved_at'> & { id?: string };

export const hayVersionVigente = (versiones: VersionMin[]) => versiones.some(v => v.status === 'aprobada');

export function estadoPO16(versiones: VersionMin[]): EstadoItem {
  return hayVersionVigente(versiones) ? 'cumple' : 'no_cumple';
}

export function estadoPO18(acciones: AccionMin[], versiones: VersionMin[], hoy: Date = new Date(), conEvidencia: Set<string> = new Set()): EstadoItem {
  // PE-A4c (decisión de JC): un plan sin ninguna acción no tiene implementación que mostrar: PO-18 queda pendiente,
  // aunque haya una versión aprobada. Con acciones pero sin obligatorias vigentes sigue la regla de PE-39.
  if (acciones.length === 0) return 'pendiente';
  const obligatorias = acciones.filter(a => a.is_mandatory && !a.source_resolved_at);
  if (obligatorias.length === 0) return hayVersionVigente(versiones) ? 'cumple' : 'pendiente';
  const cerradaConEvidencia = (a: AccionMin) => a.status === 'cerrada' && (!!a.evidence_path?.trim() || (!!a.id && conEvidencia.has(a.id)));
  const abiertas = obligatorias.filter(a => !cerradaConEvidencia(a));
  if (abiertas.length === 0) return 'cumple';
  const hoyIso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
  return abiertas.some(a => !!a.due_date && a.due_date < hoyIso) ? 'no_cumple' : 'en_proceso';
}

export interface EstadoGuardadoChecklist { item_id: string; estado: string; auto_source: string | null }

// ¿El ítem tiene un estado manual? Fila con auto_source NULL que no es "No aplica".
export const esManual = (e: EstadoGuardadoChecklist | undefined) => !!e && e.auto_source === null && e.estado !== 'no_aplica';

// ¿Qué se escribe? null = nada. Lo manual (auto_source NULL) nunca se reemplaza (PE-B2b).
export function decisionPlanV2(existente: EstadoGuardadoChecklist | undefined, deseado: EstadoItem): EstadoItem | null {
  if (existente?.estado === 'no_aplica') return null;
  if (esManual(existente)) return null; // estado manual: lo decide la persona
  if (existente?.estado === deseado && existente.auto_source === FUENTE_PLAN_V2) return null;
  if (!existente && deseado === 'pendiente') return null;
  return deseado;
}

export async function usaPlanV2(companyId: string, planId: string): Promise<boolean> {
  const cuenta = async (tabla: string) => {
    const { count } = await supabase.from(tabla).select('id', { count: 'exact', head: true }).eq('company_id', companyId).eq('plan_id', planId);
    return count ?? 0;
  };
  return (await cuenta('emergency_plan_versions')) > 0 || (await cuenta('emergency_plan_sectors')) > 0 || (await cuenta('emergency_assessments')) > 0
    || (await cuenta('emergency_drills')) > 0;
}

// Lo que se muestra en el ítem del Checklist: el estado que calcula el módulo, su traza y, si hay un estado manual,
// cuál es (el cálculo no lo pisa; "Usar cálculo automático" lo aplica).
export interface TrazaItemPlan {
  estado: EstadoItem; resumen: string; trazas: Traza[];
  manualRespetado: boolean;          // hay un estado manual y se mantuvo
  estadoManual: string | null;
  // F3a: otro módulo que usa la misma presentación (Inspecciones, PO-14/15/23). Sin estos campos, es el Plan de Emergencias.
  modulo?: string;
  fuente?: string;
}
export type TrazasPlanV2 = Record<string, TrazaItemPlan>;

const ETIQUETA: Record<EstadoItem, string> = { cumple: 'cumple', en_proceso: 'en proceso', no_cumple: 'no cumple', pendiente: 'pendiente' };
const estadoTraza = (e: EstadoItem): Traza['estado'] => (e === 'cumple' ? 'cumple' : e === 'no_cumple' ? 'no_cumple' : 'por_validar');

export function resultadoPO16(versiones: VersionMin[]): { estado: EstadoItem; resumen: string; trazas: Traza[] } {
  const estado = estadoPO16(versiones);
  const aprobadas = versiones.filter(v => v.status === 'aprobada').length;
  const resumen = aprobadas ? 'El plan tiene una versión aprobada vigente.' : 'El plan no tiene ninguna versión aprobada.';
  return {
    estado, resumen, trazas: [{
      metodo: 'po16_version', norma: 'Plan de Emergencias v2', articulo_o_tabla: 'versión aprobada vigente del plan', umbral: 1,
      valor: aprobadas, unidad: 'versiones', estado: estadoTraza(estado), fuente_doc: 'Anexo 1, Lista de verificación de cumplimiento de obligaciones de seguridad y salud en el trabajo (Ministerio del Trabajo), PO 16', nota: resumen,
    }],
  };
}

export function resultadoPO18(acciones: AccionMin[], versiones: VersionMin[], hoy: Date, conEvidencia: Set<string>): { estado: EstadoItem; resumen: string; trazas: Traza[] } {
  const estado = estadoPO18(acciones, versiones, hoy, conEvidencia);
  const obligatorias = acciones.filter(a => a.is_mandatory && !a.source_resolved_at);
  const cerradas = obligatorias.filter(a => a.status === 'cerrada' && (!!a.evidence_path?.trim() || (!!a.id && conEvidencia.has(a.id)))).length;
  const resumen = acciones.length === 0 ? 'El plan no tiene acciones.'
    : `${plural(acciones.length, 'acción', 'acciones')} en el plan; ${cerradas} de ${plural(obligatorias.length, 'obligatoria', 'obligatorias')} cerradas con evidencia.`;
  return {
    estado, resumen, trazas: [{
      metodo: 'po18_acciones', norma: 'Plan de acción del plan de emergencia', articulo_o_tabla: 'obligatorias cerradas con evidencia y sin vencidas',
      umbral: obligatorias.length, valor: cerradas, unidad: 'acciones', estado: estadoTraza(estado),
      fuente_doc: 'Criterio técnico de StorSafe (por validar)', nota: resumen, // interno: PASO0_DISENO.md §7 PE-39
    }],
  };
}

// "Usar cálculo automático": quita el estado manual y escribe el cálculo con la fuente del plan. Nunca sobre "No aplica".
export async function aplicarCalculoPlanV2(companyId: string, itemId: string, estado: EstadoItem): Promise<void> {
  const { data, error } = await supabase.from('checklist_item_estados').select('estado').eq('company_id', companyId).eq('item_id', itemId).maybeSingle();
  if (error) throw new Error(`No se pudo leer el estado de ${itemId}: ${error.message}`);
  if ((data as { estado: string } | null)?.estado === 'no_aplica') throw new Error(`${itemId} está marcado como "No aplica": no se cambia.`);
  const { error: e2 } = await supabase.from('checklist_item_estados').upsert(
    { company_id: companyId, item_id: itemId, estado, auto_source: FUENTE_PLAN_V2, updated_at: new Date().toISOString() },
    { onConflict: 'company_id,item_id' });
  if (e2) throw new Error(e2.code === '42501' ? 'No tienes permiso para cambiar el Checklist.' : `No se pudo aplicar el cálculo en ${itemId}: ${e2.message}`);
}
export const etiquetaEstadoItem = (e: EstadoItem) => ETIQUETA[e];

const COLUMNAS_PO17 = 'status, drill_date, performed_date, scheduled_date, report_signed_at';

export async function syncPlanEmergenciasCompliance(companyId: string, hoy: Date = new Date()): Promise<TrazasPlanV2> {
  if (!companyId) return {};
  const { data: plan } = await supabase.from('emergency_plans').select('id, canton_code').eq('company_id', companyId).maybeSingle();
  const planId = (plan as { id: string } | null)?.id;
  if (!planId || !(await usaPlanV2(companyId, planId))) return {};
  const quito = (plan as { canton_code?: string | null }).canton_code === '1701';
  const [{ data: versiones }, { data: acciones }, { data: evidencias, error: errorEvidencias }, { data: simulacros, error: errorSimulacros }, { data: existentes }] = await Promise.all([
    supabase.from('emergency_plan_versions').select('status').eq('plan_id', planId),
    supabase.from('emergency_plan_actions').select('id, is_mandatory, status, evidence_path, due_date, source_resolved_at').eq('plan_id', planId),
    supabase.from('emergency_plan_action_evidence').select('action_id').eq('plan_id', planId),
    // Todos los simulacros de la empresa (los de la pantalla anterior no tienen plan_id). Sin M38 en la base faltan
    // performed_date, scheduled_date y report_signed_at: PO-17 no se toca.
    supabase.from('emergency_drills').select(COLUMNAS_PO17).eq('company_id', companyId),
    supabase.from('checklist_item_estados').select('item_id, estado, auto_source').eq('company_id', companyId).in('item_id', ['PO-16', 'PO-17', 'PO-18']),
  ]);
  if (errorEvidencias) console.error('syncPlanEmergenciasCompliance: evidencias', errorEvidencias);
  if (errorSimulacros) console.error('syncPlanEmergenciasCompliance: simulacros', errorSimulacros);
  const conEvidencia = new Set(((evidencias ?? []) as { action_id: string }[]).map(e => e.action_id));
  const porItem = new Map(((existentes ?? []) as EstadoGuardadoChecklist[]).map(e => [e.item_id, e]));
  const po17: ResultadoPO17 | null = errorSimulacros ? null : estadoPO17((simulacros ?? []) as Parameters<typeof estadoPO17>[0], hoy, quito);
  const resultados: [string, { estado: EstadoItem; resumen: string; trazas: Traza[] }][] = [
    ['PO-16', resultadoPO16((versiones ?? []) as VersionMin[])],
    ...(po17 ? [['PO-17', po17] as [string, ResultadoPO17]] : []),
    ['PO-18', resultadoPO18((acciones ?? []) as AccionMin[], (versiones ?? []) as VersionMin[], hoy, conEvidencia)],
  ];
  const ahora = new Date().toISOString();
  const filas = resultados.flatMap(([item, r]) => {
    const d = decisionPlanV2(porItem.get(item), r.estado);
    return d ? [{ company_id: companyId, item_id: item, estado: d, auto_source: FUENTE_PLAN_V2, updated_at: ahora }] : [];
  });
  if (filas.length) await supabase.from('checklist_item_estados').upsert(filas, { onConflict: 'company_id,item_id' });
  return Object.fromEntries(resultados.map(([item, r]) => {
    const previo = porItem.get(item);
    const manual = esManual(previo);
    return [item, { estado: r.estado, resumen: r.resumen, trazas: r.trazas, manualRespetado: manual, estadoManual: manual ? previo!.estado : null }];
  }));
}
